import { LinkExternal01Icon } from '@langchain/untitled-ui-icons';
import Box from '@mui/joy/Box';
import Link from '@mui/joy/Link';
import { SystemStyleObject } from '@mui/system';

import { Link as RouterLink } from 'react-router-dom';

import { LatencyChip } from '@/components/RunLatencyChip';
import { useOrganizationId } from '@/hooks/useSwr';
import { getDatasetPath } from '@/utils';

import {
  DatasetSchema,
  RunStatsSchema,
  SessionSchema,
} from '../../../types/schema';

type LabelPlacement = 'inside' | 'outside';

export function SessionLatencyCell({
  row,
  label,
  labelPlacement = 'inside',
  size = 'xs',
  metric,
  testId,
  sessionStats,
}: {
  row: RunStatsSchema;
  metric?:
    | 'latency_p50'
    | 'latency_p99'
    | 'first_token_p50'
    | 'first_token_p99';
  size?: 'xs' | 'sm';
  label?: string;
  labelPlacement?: LabelPlacement;
  testId?: string;
  sessionStats?: {
    latency_p50?: number;
    latency_p99?: number;
    first_token_p50?: number;
    first_token_p99?: number;
  };
}) {
  const latency = row[metric ?? 'latency_p50'];
  const latencyP50 = metric?.includes('latency')
    ? sessionStats?.latency_p50
    : sessionStats?.first_token_p50;
  const latencyP99 = metric?.includes('latency')
    ? sessionStats?.latency_p99
    : sessionStats?.first_token_p99;

  if (labelPlacement === 'inside') {
    return (
      <LatencyChip
        testId={testId}
        latency={latency}
        label={label}
        size={size}
        percentiles={{ p50: latencyP50, p99: latencyP99 }}
      />
    );
  }
  return (
    <div className="flex items-center gap-1 rounded-md border border-secondary bg-background p-1">
      <div className="pl-1 text-xs font-medium text-tertiary">{label}</div>
      <LatencyChip
        testId={testId}
        latency={latency}
        size={size}
        percentiles={{ p50: latencyP50, p99: latencyP99 }}
      />
    </div>
  );
}

export function ReferenceDatasetCell({
  label,
  dataset,
  sx,
}: {
  label?: string;
  dataset?: DatasetSchema;
  row?: SessionSchema;
  sx?: SystemStyleObject;
}) {
  const organizationId = useOrganizationId();
  if (!dataset || !organizationId) return null;
  return (
    <Box display="flex" gap={1} alignItems="center" sx={sx}>
      <Link
        fontSize="inherit"
        component={RouterLink}
        startDecorator={
          <LinkExternal01Icon className="size-4" fontSize="inherit" />
        }
        to={getDatasetPath(dataset.id, organizationId)}
        onClick={(e) => e.stopPropagation()}
        sx={{
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
        {dataset.name}
      </Link>
    </Box>
  );
}
